<template>
<div :class="rootClass" v-if="pendingReady && affiliationsList.length">
    <p class="affil-heading">Institution Invitations</p>

    <div class="affil-row" v-for="invite in affiliationsList" :key="invite.id">
        <span class="affil-mark" :style="{ backgroundColor: colorFor(invite) }">{{ initialsFor(invite) }}</span>
        <span class="affil-body">
            <span class="affil-name" :title="invite.institutionName">Affiliate with {{ invite.institutionName || 'an institution' }}</span>
            <span class="affil-copy">You'll get a Cuelist license paid for by the institution. They can deactivate or remove it later.</span>
        </span>
        <span class="affil-btns">
            <button type="button" class="affil-btn affil-accept" @click="accept(invite)">Accept</button>
            <button type="button" class="affil-btn affil-decline" @click="decline(invite)">Decline</button>
        </span>
    </div>
</div>
</template>

<script>
/**
 * Institution Invitations panel. Mirrors PendingProjects: driven by the
 * live-bound userAffiliateInvitations getter (Firestore listener), so an invite
 * appears with no refresh — including for a user who had no account when invited
 * (reconciled on login by the email-scoped binding in dashboard.vue, and by the
 * matching binding in account.vue). Accepting affiliates the user with the
 * institution; the CF then rosters them and consumes a seat if one is free. The
 * panel hides itself when empty.
 *
 * TWO VARIANTS, ONE MARKUP (Josh, 8 Sept 2026). The component now renders in two
 * places that look nothing alike:
 *
 *   variant="panel"  (default) — the dashboard. A grey panel on the page,
 *                    matching its sibling PendingProjects directly above it.
 *   variant="card"   — inside the account page's Subscription card. No panel
 *                     background at all: a rule and a section heading, drawn
 *                     entirely from the account page's own CSS variables.
 *
 * The variants differ ONLY in CSS. The markup, the props and every method are
 * shared, so copy changes and logic fixes cannot land in one place and miss the
 * other — the failure this component would otherwise have invited by being
 * duplicated. The row is flex in the panel and grid in the card, which is what
 * moves the buttons from beside the text to beneath it.
 *
 * Plain markup/CSS (no vue-material) so this adds no Vue 3 migration debt.
 */
import { mapGetters } from 'vuex';
import { coverInitials, coverColor } from '@/lib/projectCover';

const PendingAffiliations = {
    name: 'PendingAffiliations',
    props: {
        user: { type: Object, default: null },
        pendingReady: { type: Boolean, default: false },
        // 'panel' = dashboard grey panel; 'card' = inside an account-page card.
        variant: {
            type: String,
            default: 'panel',
            validator: v => ['panel', 'card'].includes(v)
        }
    },
    computed: {
        ...mapGetters([
            'user',
            'userAffiliateInvitations'
        ]),
        affiliationsList() {
            return this.userAffiliateInvitations;
        },
        rootClass() {
            return this.variant === 'card' ? 'affil affil-as-card' : 'affil affil-as-panel';
        }
    },
    methods: {
        accept(invite) {
            // Josh, 23 Sept 2026: someone with their own live subscription cannot
            // join an institution's license. Say why, and mark the invite failed so
            // the admin sees the reason rather than an invite stuck on pending.
            // A subscription already set to cancel does not block.
            const me = this.user || {};
            if (me.active_pay && me.subscription_id && !me.pending_cancel) {
                const org = invite.institutionName || 'this institution';
                // eslint-disable-next-line no-alert
                window.alert(`You can't join ${org}'s license because you already pay for your own Cuelist subscription. To join instead, cancel your subscription first, then accept this invitation.`);
                this.$store.dispatch('markAffiliateInvitationFailed', {
                    invitationID: invite.id,
                    reason: 'has_own_subscription'
                });
                return;
            }
            // eslint-disable-next-line no-alert
            if (!window.confirm(`Affiliate with ${invite.institutionName || 'this institution'}? You'll join their Cuelist license.`)) return;
            this.$store.dispatch('acceptAffiliateInvitation', { invitation: invite }).then(() => {
                this.$emit('affiliationAccepted', invite.adminUid);
            }).catch(() => {
                // Was silent: the invite simply reappeared as if never clicked.
                this.$toast.error('Could not accept the invitation. Please try again.');
            });
        },
        decline(invite) {
            // Decline permanently deletes the invite (no undo, and the admin
            // can't tell decline from pending) — confirm before destroying it.
            // eslint-disable-next-line no-alert
            if (!window.confirm(`Decline the invitation to affiliate with ${invite.institutionName || 'this institution'}?`)) return;
            this.$store.dispatch('declineAffiliateInvitation', { invitationID: invite.id });
        },
        initialsFor(invite) {
            return coverInitials(invite.institutionName || 'Institution');
        },
        colorFor(invite) {
            return coverColor(invite.adminUid || invite.id);
        }
    },
    components: {}
};
export default PendingAffiliations;
</script>

<style lang="scss">
/* Shared by both variants. Everything that differs is under the variant class. */
.affil {
    .affil-mark {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-weight: 700;
    }
    .affil-body {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
    }
    .affil-btns {
        flex: 0 0 auto;
        display: flex;
        gap: 6px;
    }
    .affil-btn {
        cursor: pointer;
        font-family: inherit;
        font-weight: 600;
    }
}

/* ── Dashboard: grey panel, matching its PendingProjects sibling ───────────── */
.affil-as-panel {
    background: #e9e9e9;
    border-radius: 8px;
    padding: 16px 18px;

    .affil-heading {
        margin: 0 0 12px;
        font-size: 20px;
        font-weight: 500;
        color: #000;
    }
    .affil-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 0;
    }
    .affil-mark {
        width: 36px;
        height: 36px;
        border-radius: 4px;
        font-size: 14px;
    }
    .affil-name {
        font-weight: 600;
        color: #333;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .affil-copy {
        font-size: 13px;
        color: #888;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .affil-btn {
        border: 1px solid transparent;
        border-radius: 4px;
        padding: 6px 12px;
        font-size: 13px;
    }
    /* Accept carries the accent, matching the sibling panel's action buttons.
       Decline was #e5828a — a red that appears nowhere else in the product
       (Josh, 8 Sept: "not part of my current color scheme"). A destructive
       secondary action reads better unfilled anyway, so it is now an outline
       button that borrows the panel's own greys. */
    .affil-accept {
        background: var(--accent);
        color: #fff;
        &:hover { background: #4dbccb; }
    }
    .affil-decline {
        background: transparent;
        color: #555;
        border-color: #bdbdbd;
        &:hover { background: #dedede; border-color: #a8a8a8; }
    }
}

/* Dashboard dark mode, mirroring pending-projects.vue's own block. */
.md-theme-show-mode {
    .affil-as-panel {
        background: #2b3940;

        .affil-heading { color: rgba(255, 255, 255, 0.9); }
        .affil-name    { color: rgba(255, 255, 255, 0.92); }
        .affil-copy    { color: rgba(255, 255, 255, 0.6); }
        .affil-decline {
            color: rgba(255, 255, 255, 0.82);
            border-color: rgba(255, 255, 255, 0.34);
            &:hover {
                background: rgba(255, 255, 255, 0.10);
                border-color: rgba(255, 255, 255, 0.5);
            }
        }
    }
}

/* ── Account page: no panel. Lives inside the Subscription card and takes all
      of its colour from the .acct2 variables, so .acct2.is-dark drives it with
      no extra rules. Buttons deliberately reuse the card's own btn shapes. ─── */
.affil-as-card {
    margin-top: 22px;
    padding-top: 20px;
    border-top: 1px solid var(--border);

    .affil-heading {
        margin: 0 0 12px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: .1em;
        text-transform: uppercase;
        color: var(--accent-ink);
    }
    .affil-row {
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-areas:
            "mark body"
            ".    btns";
        gap: 0 12px;
        align-items: start;
        padding: 12px 14px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--surface-2);

        + .affil-row { margin-top: 9px; }
    }
    .affil-mark {
        grid-area: mark;
        width: 34px;
        height: 34px;
        border-radius: 8px;
        font-size: 13px;
    }
    .affil-body { grid-area: body; }
    .affil-name {
        font-weight: 650;
        font-size: 14.5px;
        color: var(--text);
    }
    .affil-copy {
        font-size: 13px;
        color: var(--text-muted);
        margin-top: 3px;
    }
    .affil-btns {
        grid-area: btns;
        margin-top: 11px;
    }
    .affil-btn {
        border: 1px solid transparent;
        border-radius: 7px;
        padding: 7px 13px;
        font-size: 13px;
    }
    .affil-accept {
        background: var(--btn-primary-bg);
        color: var(--btn-primary-fg);
        &:hover { background: var(--btn-primary-hover); }
    }
    .affil-decline {
        background: transparent;
        color: var(--text);
        border-color: var(--border-strong);
        /* --surface, not --surface-2: the row itself is --surface-2, so hovering
           to it would be a no-op. --surface is lighter than the row in both
           themes, so the button lifts either way. */
        &:hover { background: var(--surface); border-color: var(--text-muted); }
    }
}
</style>


// WEBPACK FOOTER //
// pending-affiliations.vue?7bec49f9